import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Spinner from 'components/Spinner';

import 'mapbox-gl/dist/mapbox-gl.css';
import { useDistributionStore } from 'apiTyped/hooks/distribution.query';
import { General } from './GeneralTab';
import { NavigationItemKey, StoreNavigationTab } from './Navigation';
import { KeyIndicators } from './KeyIndicators';
import { WineListTab } from './DocumentTab';

const Overlay = styled.div<{ open: boolean }>`
  display: ${(p) => (p.open ? 'flex' : 'none')};
  position: fixed;
  inset: 0;
  align-items: center;
  justify-content: center;
  background: rgba(20, 28, 38, 0.5);
  backdrop-filter: blur(3px);
  z-index: 1000;
  font-family:
    'Inter',
    -apple-system,
    sans-serif;
  animation: smFadeIn 0.26s ease;

  @keyframes smFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const PopupCard = styled.div`
  width: 700px;
  min-height: 740px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 4px;
  box-shadow:
    0 20px 50px rgba(0, 0, 0, 0.18),
    0 8px 20px rgba(0, 0, 0, 0.08);
  animation: smSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;

  @keyframes smSlideUp {
    from {
      opacity: 0;
      transform: translateY(16px) scale(0.985);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const PopupHeader = styled.div`
  background: #2b3d54;
  height: 54px;
  padding: 0 26px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #ffffff;
    letter-spacing: 0.01em;
    text-transform: uppercase;
  }

  .close-btn {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 1.5px solid rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.6);
    font-size: 13px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    line-height: 1;

    &:hover {
      border-color: rgba(255, 255, 255, 0.6);
      color: #ffffff;
      background: rgba(255, 255, 255, 0.1);
    }
  }
`;

const StoreModalContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;

  .tabs-container {
    display: flex;
    gap: 24px;
    padding: 0 26px;
    height: 56px;
    border-bottom: 1px solid #e3e8ed;
    flex-shrink: 0;
    align-items: center;

    .tab {
      position: relative;
      height: 100%;
      padding: 0;
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      border: none;
      background: none;
      cursor: pointer;
      color: #a8b5c2;
      transition: color 0.2s;

      &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        height: 2px;
        border-radius: 1px;
        background: #e25d34;
        transform: scaleX(0);
        transition: transform 0.22s ease;
      }

      &.active {
        color: #e25d34;
        &::after {
          transform: scaleX(1);
        }
      }

      &:hover:not(.active) {
        color: #4a5868;
      }
    }
  }

  .tab-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 2px 26px 26px;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(148, 163, 184, 0.2);
      border-radius: 10px;
    }
  }

  /* Kept for KeyIndicators */
  .info-section {
    margin-bottom: 20px;

    .section-title {
      font-size: 11px;
      font-weight: 700;
      color: #4a5868;
      text-transform: uppercase;
      letter-spacing: 0.07em;
      padding: 16px 0 6px;
    }

    .info-row {
      display: flex;
      padding: 8px 0;
      border-bottom: 1px solid #eef1f4;
      align-items: center;

      &:last-child {
        border-bottom: none;
      }

      .label {
        font-weight: 600;
        color: #1f2a33;
        min-width: 200px;
        font-size: 13px;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .value {
        color: #1f2a33;
        font-size: 13px;
        word-break: break-word;
        flex: 1;
      }
    }
  }
`;

type StoreModalProps = {
  storeId: string | null;
  isOpen: boolean;
  onClose: () => void;
  displayDocumentsTab?: boolean;
};

const StoreModal: React.FC<StoreModalProps> = ({
  storeId,
  isOpen,
  displayDocumentsTab = false,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<NavigationItemKey>(
    NavigationItemKey.GENERAL
  );

  const { isLoading, data: storeData } = useDistributionStore(storeId || '');

  const hasDocumentsTab = displayDocumentsTab && !!storeData?.documents?.length;

  useEffect(() => {
    setActiveTab(
      hasDocumentsTab ? NavigationItemKey.DOCUMENTS : NavigationItemKey.GENERAL
    );
  }, [hasDocumentsTab]);

  if (!isOpen || !storeId) {
    return null;
  }

  if (isLoading || !storeData) {
    return (
      <Overlay open={isOpen} onClick={onClose}>
        <PopupCard onClick={(e) => e.stopPropagation()}>
          <PopupHeader>
            <h2>&nbsp;</h2>
            <button className="close-btn" onClick={onClose}>
              ✕
            </button>
          </PopupHeader>
          <Spinner loading={true} />
        </PopupCard>
      </Overlay>
    );
  }

  return (
    <Overlay open={isOpen} onClick={onClose}>
      <PopupCard onClick={(e) => e.stopPropagation()}>
        <PopupHeader>
          <h2>{storeData.name}</h2>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </PopupHeader>

        <StoreModalContent>
          <StoreNavigationTab
            displayDocumentsTab={hasDocumentsTab}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />

          <div className="tab-content">
            {activeTab === NavigationItemKey.GENERAL && (
              <General {...storeData} />
            )}
            {activeTab === NavigationItemKey.DOCUMENTS && (
              <WineListTab {...storeData} />
            )}
            {activeTab === NavigationItemKey.KEY_INDICATORS && (
              <KeyIndicators
                {...storeData}
                dn_total={storeData.dn_total ?? 0}
              />
            )}
          </div>
        </StoreModalContent>
      </PopupCard>
    </Overlay>
  );
};

export default StoreModal;
